import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import useTable from '../TablePagination/UseTable';
import TablePagination from '../TablePagination/TablePagination';
import dayjs from 'dayjs';
import HIMInventoryModalPopup from './HIMInventoryModalPopup';
import { IHIMInventoryItem } from '../Infrastructure/Interfaces';
import { setHIMInventoryItem } from '../Store/HIMInventoryReducer';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../Store/StoreInit';

function HIMInventoryPage() {
  // State variables
  const [validationData, setValidationData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [opened, setOpened] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedRegionCode, setSelectedRegionCode] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Hooks
  const {
    isOpen: isOpenConfirm,
    onOpen: onOpenConfirm,
    onClose: onCloseConfirm,
  } = useDisclosure();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [claimsPerPage, setClaimsPerPage] = useState<number>(10);
  const lastIndex = currentPage * claimsPerPage;
  const firstIndex = lastIndex - claimsPerPage;
  const arrregioncodes: string[] = (process.env.REACT_APP_REGION_CODES as string).split(',');
  const dispatch = useDispatch<AppDispatch>();

  // API call to get list of inventory reports
  const fetchAccountValidation = async () => {
    try {
      // Validation of URLs
      const baseurl = process.env.REACT_APP_SERVER_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_SIS as string;

      // Get auth token
      const tokenResponse = await axios.get(tokenUrl);

      // If data is retrieved, filter data based on search bar
      if (tokenResponse.data.success) {
        const userdetails = JSON.parse(
          localStorage.getItem('userdetails') || ''
        );
        let userRole = userdetails[0].userRole!;
        let regionCode = userdetails[0].regionCode!;

        const validationResponse = await axios.get(
          `${baseurl}/Him/Inventory/${userRole}/${regionCode}`,
          {
            headers: {
              Pragma: 'no-cache',
              Authorization: 'Bearer ' + tokenResponse.data.token,
            },
          }
        );

        if (validationResponse.data.success) {
          setCopied(false);

          const filteredData = validationResponse.data.result
            .filter(
              (item: any) =>
                item.RegionCode === 'PCOE' || item.RegionCode === 'PPCOE'
            )
            .filter((item: any) =>
              [
                item.AccountNo,
                item.RegionCode,
                item.ServiceType,
                item.CodeType,
                item.HealthplanName,
                item.DeliveryOption,
                item.nextDeliveryOption,
                item.currentAccBalance,
              ].some(
                (value: any) =>
                  value &&
                  value.toLowerCase().includes(searchQuery.toLowerCase())
              )
            );

          // Calculate time difference and add it as a property to each item
          const dataWithCompletionTime = filteredData.map((item: any) => {
            const timeDiff = calculateTimeDifference(item.CodeDate, new Date());
            const totalHours = timeDiff.days() * 24 + timeDiff.hours();
            const minutes = timeDiff.minutes();
            return {
              ...item,
              completionTime: totalHours * 60 + minutes, // Convert to total minutes for easier sorting
            };
          });

          // Sort by completion time
          const sortedData = dataWithCompletionTime.sort(
            (a: any, b: any) => a.completionTime - b.completionTime
          );

          setValidationData(filteredData);
        } else {
          throw new Error('Failed to fetch validation data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error fetching validation data:', error);
    }
  };

  // Calculate the time difference in days, hours, and minutes
  function calculateTimeDifference(CodeDate: any, todayDateAndTime: any) {
    const codeDate = dayjs(CodeDate);
    const toBeCompletedBy = dayjs(todayDateAndTime);
    const futureDate = codeDate.add(4, 'days');

    const duration = dayjs.duration(futureDate.diff(toBeCompletedBy));
    return duration;
  }

  // API call to get single report data for validation of user Owner
  const getSingleReport = async (id: any, item: any) => {
    var data = validationData.find(i => i.WorkListInventoryId == id);

    const invitem: IHIMInventoryItem = {
      WorkListInventoryId: id,
      appealId: data?.appealId!,
      MRN: data?.MRN!,
      AccountNo: data?.AccountNo!,
      RegionCode: data?.RegionCode!,
      PlanType: data?.PlanType!,
      HealthplanName: data?.HealthplanName!,
      Remarks: data?.Remarks!,
      CodeType: data?.CodeType!,
      MD_NoOfpages: data?.MD_NoOfpages!,
      p_name: data?.p_name!,
      P_DOB: data?.P_DOB!,
      ClaimNumber: data?.ClaimNumber!,
      totalCharges: data?.totalCharges!,
      insuranceBalance: data?.insuranceBalance!,
      currentAccBalance: data?.currentAccBalance!,
      patientBalance: data?.patientBalance!,
      DeliveryOption: data?.DeliveryOption!,
      p_AdmitDate: data?.p_AdmitDate!,
      p_DischargeDate: data?.p_DischargeDate!,
      MergeFilePath: data?.MergeFilePath!,
      transactionState: data?.transactionState!,
      workQueueInventoryStatus: data?.workQueueInventoryStatus!,
      LockedBy: data?.LockedBy!,
      ServiceType: data?.ServiceType!,
      nextDeliveryOption: data?.nextDeliveryOption!,
      TaskName: data?.TaskName!,
      TaskNote: data?.TaskNote!,
      documents: data?.documents!,
      Documentstobeincluded: data?.Documentstobeincluded!,
      DocumentLiseted: data?.DocumentLiseted!,
      Coverletterpayer: data?.Coverletterpayer!,
      TypeOfCoverLetter: data?.TypeOfCoverLetter!,
      InsurancePlan: data?.InsurancePlan!,
      MethodofDelivery: data?.MethodofDelivery!,
      PortalName: data?.PortalName!,
      PayerAddress: data?.PayerAddress!,
      SendRecordsTo: data?.SendRecordsTo!,
      ClaimFormRequired: data?.ClaimFormRequired!,
      CoverLetterCreatedby: data?.CoverLetterCreatedby!,
      DeadLine: data?.DeadLine!,
      EncounterClaimNo: data?.EncounterClaimNo!,
    };
    dispatch(setHIMInventoryItem(invitem));

    try {
      const baseurl = process.env.REACT_APP_SERVER_BASE_URL as string;
      const tokenUrl = process.env.REACT_APP_TOKEN_URL_SIS as string;

      const tokenResponse = await axios.get(tokenUrl);
      if (tokenResponse.data.success) {
        const validationResponse = await axios.get(
          `${baseurl}/Him/Inventory/${id}`,
          {
            headers: {
              Pragma: 'no-cache',
              Authorization: 'Bearer ' + tokenResponse.data.token,
            },
          }
        );

        if (validationResponse.data.success) {
          setCopied(true);

          const singleReport = validationResponse.data.result;
          if (
            singleReport[0].lockedby &&
            userEmail !== singleReport[0].lockedby
          ) {
            toast({
              title: 'Permission denied',
              position: 'top',
              description:
                'This Item is already being edited by: ' +
                singleReport[0].lockedby,
              status: 'error',
              duration: 9000,
              isClosable: true,
            });
          } else if (
            userEmail === singleReport[0].lockedby ||
            !singleReport[0].lockedby
          ) {
            setSelectedItem(item);
            onOpenConfirm();
          }
        } else {
          throw new Error('Failed to fetch validation data');
        }
      } else {
        throw new Error('Failed to retrieve auth token');
      }
    } catch (error) {
      console.error('Error fetching validation data:', error);
    }
  };

  useEffect(() => {
    fetchAccountValidation();
  }, [copied, opened, searchQuery]);

  // Handle Region filter dropdown
  const handleRegionCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRegionCode(e.target.value);
  };

  // Handle the data filtered from search bar
  const filteredData = selectedRegionCode
    ? validationData.filter(item => item.RegionCode === selectedRegionCode)
    : validationData;

  const { slice, range } = useTable(
    filteredData.length > 0 ? filteredData : filteredData,
    currentPage,
    claimsPerPage
  );

  return (
    <>
      {validationData ? (
        <Box display=" flex" flexDirection="column">
          <Flex display="flex" justifyContent="flex-start" flexDir="row">
            <Text pt="2px" mr="5px" fontWeight="bold">
              Region:
            </Text>
            <Select
              borderRadius="10px"
              value={selectedRegionCode}
              onChange={handleRegionCodeChange}
              placeholder="ALL"
              size="sm"
              width="150px"
              mb="5px"
              border="1px solid #d3d3d3"
            >
              {arrregioncodes?.map(regioncode => (
                <option key={regioncode} value={regioncode}>
                  {regioncode}
                </option>
              ))}
            </Select>
            <Input
              marginBottom="5px"
              marginTop="15px"
              htmlSize={24}
              width="auto"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              marginLeft="auto" // Add this line
            />
          </Flex>

          <TableContainer borderRadius="5px" borderTopColor="black" w="100%">
            <Table
              variant="striped"
              colorScheme="blackAlpha"
              size="sm"
              overflow="auto"
            >
              <Thead bgColor="#002677">
                <Tr>
                  <Th color="white">Region Code</Th>
                  <Th color="white">Account Number</Th>
                  <Th color="white">Service Type</Th>
                  <Th color="white">Code Type</Th>
                  <Th color="white">Health Plan Name</Th>
                  <Th color="white">Delivery Option</Th>
                  <Th color="white">Next Delivery Option</Th>
                  <Th color="white">Current Balance </Th>
                  <Th color="white" isNumeric>
                    VALIDATION
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredData.slice(firstIndex, lastIndex).map(item => {

                  return (
                    <Tr
                      key={item.AccountNo}
                      bg={
                        item.LockedBy !== null
                          ? item.LockedBy === userEmail
                            ? 'orange'
                            : 'red'
                          : 'none'
                      }
                    >
                      <Td>{item.RegionCode}</Td>
                      <Td>{item.AccountNo}</Td>
                      <Td>{item.ServiceType ? item.ServiceType : 'N/A'}</Td>
                      <Td>{item.CodeType}</Td>
                      <Td>{item.HealthplanName}</Td>
                      <Td>
                        {item.DeliveryOption ? item.DeliveryOption : 'N/A'}
                      </Td>
                      <Td>
                        {item.nextDeliveryOption
                          ? item.nextDeliveryOption
                          : 'N/A'}
                      </Td>
                      <Td>{item.currentAccBalance}</Td>
                      <Td pt="0" pb="0" mt="0" mb="0" pr="50px" isNumeric>
                        <Flex justifyContent="flex-end">
                          <FaCheck
                            onClick={() => {
                              setOpened(true);
                              getSingleReport(item.WorkListInventoryId, item);
                            }}
                          />
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
            <Flex display="flex" flexDirection="row">
              <Flex />
              <Spacer />
              <TablePagination
                range={range}
                slice={slice}
                setPage={setCurrentPage}
                page={currentPage}
              />
              <Spacer />
              <Select
                value={claimsPerPage}
                onChange={e => setClaimsPerPage(parseInt(e.target.value))}
                width="100px"
                height="30px"
                mt="5px"
                mr="5px"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </Flex>
          </TableContainer>
        </Box>
      ) : (
        ''
      )}
      <HIMInventoryModalPopup
        isOpen={isOpenConfirm}
        onClose={onCloseConfirm}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        setCopied={setCopied}
      />
    </>
  );
}

export default HIMInventoryPage;
